import type { IdTokenResult } from 'firebase/auth';
import type { UserRole } from '../../types/user';

// Mapeo de roles: el backend firma los claims en español
// (docs/Paquete_Arranque_Frontend.md §4, backend/services/core/api/openapi.yaml
// schema `Role`); el modelo de dominio del front los usa en inglés. Este es
// el único lugar donde se traduce — nadie más debe comparar contra el
// string del claim directamente.
const ROLE_FROM_CLAIM: Record<string, UserRole> = {
  ADMINISTRADOR: 'admin',
  SUPERVISOR: 'supervisor',
  OPERADOR: 'operator',
  EJECUTIVO: 'executive',
};

export interface AuthClaims {
  tenantId: string;
  role: UserRole;
  subunitIds: string[];
}

/**
 * Traduce un rol crudo del backend (claim o `GET /me`, ambos en español) al
 * rol de dominio en inglés. Devuelve `undefined` si el string no se reconoce.
 * Único punto de traducción (#137): tanto el claim del token como `me.role`
 * usan el mismo enum `Role` del contrato.
 */
export function roleFromClaim(raw: unknown): UserRole | undefined {
  return typeof raw === 'string' ? ROLE_FROM_CLAIM[raw] : undefined;
}

/**
 * Lee y traduce los custom claims firmados por el backend. Son solo para UI
 * (qué se muestra u oculta); la autorización real la hace el backend en
 * cada request — §4 del contrato.
 */
export function readClaims(idTokenResult: IdTokenResult): AuthClaims {
  const claims = idTokenResult.claims;
  const rawRole = claims.role;
  const role = roleFromClaim(rawRole);

  if (!role) {
    throw new Error(`Claim de rol desconocido o ausente: ${String(rawRole)}`);
  }

  return {
    tenantId: typeof claims.tenant_id === 'string' ? claims.tenant_id : '',
    role,
    subunitIds: Array.isArray(claims.subunit_ids) ? (claims.subunit_ids as string[]) : [],
  };
}
